import { AppBadge, AppIconButton } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
} from '@9b3kuq5phwuk4txw/kiban-react-ui/primitives'
import { X } from 'lucide-react'
import { useEffect } from 'react'
import { getTagGroupColor, parseTagName } from '@/lib/tag-utils'
import { cn } from '@/lib/utils'
import { useTagSelect } from './hooks/useTagSelect'
import type { TagSelectProps } from './types'
import { tagToString } from './utils'

export type { TagSelectProps, TagValue } from './types'
export { parseTagString, tagToString } from './utils'

/**
 * タグ選択コンポーネント
 *
 * 既存タグからのオートコンプリート選択を行う再利用可能なコンポーネント
 * allowCreate=true の場合、新規タグの追加も可能
 */
export function TagSelect({
  value,
  onChange,
  label = 'タグを指定（複数指定可）',
  placeholder = 'タグを検索...',
  disabled = false,
  allowCreate = false,
  collapsible = false,
  helpText,
  onValidationChange,
}: TagSelectProps) {
  const {
    searchValue,
    setSearchValue,
    isOpen,
    filteredTags,
    handleAddTag,
    handleRemoveTag,
    handleKeyDown,
    handleFocus,
    handleBlur,
    isReadonlyTag,
    inputError,
  } = useTagSelect({ value, onChange, allowCreate, collapsible })

  // バリデーションエラーの変更を親に通知
  useEffect(() => {
    onValidationChange?.(!!inputError)
  }, [inputError, onValidationChange])

  return (
    <div className='space-y-2'>
      {label && <Label className='text-xs text-muted-foreground'>{label}</Label>}

      {value.length > 0 && (
        <div className='flex flex-wrap gap-1 p-2 border rounded-md bg-muted/30'>
          {value.map((tag) => {
            const tagStr = tagToString(tag)
            const parsed = parseTagName(tagStr)
            const readonly = isReadonlyTag(tag)
            return (
              <AppBadge
                key={tagStr}
                color='none'
                className={cn(readonly ? '' : 'pr-1 gap-1', getTagGroupColor(parsed.group))}
              >
                {parsed.displayName}
                {!readonly && (
                  <AppIconButton
                    size='xs'
                    onClick={() => handleRemoveTag(tag)}
                    description={`${tagStr}を削除`}
                    disabled={disabled}
                    tabIndex={-1}
                  >
                    <X className='size-3' />
                  </AppIconButton>
                )}
              </AppBadge>
            )
          })}
        </div>
      )}

      <Command className='border border-gray-400  rounded-md'>
        <CommandInput
          placeholder={placeholder}
          value={searchValue}
          onValueChange={setSearchValue}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
        />
        {isOpen && (
          <CommandList className='max-h-32'>
            <CommandEmpty>
              {searchValue.trim() && allowCreate ? (
                <span className='text-muted-foreground text-sm'>Enterキーで「{searchValue.trim()}」を追加</span>
              ) : (
                '候補がありません'
              )}
            </CommandEmpty>
            <CommandGroup heading='候補'>
              {filteredTags.map((tag) => {
                const parsed = parseTagName(tag.name)
                return (
                  <CommandItem
                    key={tag.id}
                    value={tag.name}
                    onSelect={() => handleAddTag(tag.name)}
                    disabled={disabled}
                  >
                    <AppBadge color='none' className={cn('mr-2', getTagGroupColor(parsed.group))}>
                      {parsed.displayName}
                    </AppBadge>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        )}
      </Command>

      {inputError && <p className='text-xs text-destructive'>{inputError}</p>}
      {helpText && !inputError && <p className='text-xs text-muted-foreground'>{helpText}</p>}
    </div>
  )
}
